<template>
  <div class="horizon-chart" v-loading="isLoading">
    <div :id="`horizon-chart-${agentIdLabel}`"></div>
  </div>
</template>

<script>
import { chartGlobalLabelDefaults } from '@/constants/chart-global-styles.constants'
import ApiRest from '@/api/api-rest'
import { context as cubism } from 'cubism-es'

export default {
  name: 'HorizonChart',
  props: {
    ops: {
      type: Array,
      default () {
        return ['Connects']
      }
    },
    agentId: { type: String }
  },
  data () {
    return {
      graphOptions: {
        size: { w: 400, h: 400 },
        style: chartGlobalLabelDefaults.style
      },
      isLoading: false,
      horizonChartData: []
    }
  },
  computed: {
    agentIdLabel () {
      if (this.agentId) {
        return this.agentId.replace(/#|:|\./g, '-')
      }
      return ''
    }
  },
  methods: {
    renderChart (data) {
      const vm = this
      const dataSize = 480
      const context = cubism()
        .serverDelay(60 * 1000) // allow 30 seconds of collection lag
        .step(3 * 60 * 1000) // five minutes per value
        .size(dataSize) // fetch 1920 values (1080p)

      vm.$d3
        .select(`#horizon-chart-${this.agentIdLabel}`)
        .attr('font-family', this.graphOptions.style.fontFamily)
        .selectAll('.axis')
        .data(['bottom'])
        .enter()
        .append('div')
        .attr('class', function (d) {
          return d + ' axis'
        })
        .attr('font-family', this.graphOptions.style.fontFamily)
        .each(function (d) {
          context
            .axis()
            .ticks(12)
            .orient(d)
            .render(vm.$d3.select(this))

          context.axis()._axis.tickSize(6, 3, 0)
        })

      const d = vm.$d3
        .select(`#horizon-chart-${this.agentIdLabel}`)
        .append('div')
        .attr('class', 'rule')
        .attr('id', 'rule')
      context.rule().render(d)

      vm.$d3
        .select(`#horizon-chart-${this.agentIdLabel}`)
        .selectAll('.horizon')
        .data(vm.ops.map(events))
        .enter()
        .insert('div', '.bottom')
        .attr('class', 'horizon')

      let gnRedScheme = vm.$d3.schemeGreens[6].slice(2).concat(vm.$d3.schemeReds[4])
      context
        .horizon()
        .mode('mirror')
        .height(this.graphOptions.size.h / 8)
        .colors(gnRedScheme.concat(gnRedScheme))
        // .extent([0, 50])
        .format(vm.$d3.format('.2s'))
        .render(vm.$d3.selectAll('.horizon'))

      context.on('focus', function (i) {
        if (i > dataSize) {
          vm.$d3.selectAll('.line').style('display', 'none')
        }
        vm.$d3.selectAll('.value').style('right', i == null ? null : context.size() - i + 30 + 'px')
      })

      // Replace this with context.graphite and graphite.metric!
      function events (name) {
        let value = 0
        let values = []
        let i = 0
        let last

        return context.metric(function (start, stop, step, callback) {
          start = +start
          stop = +stop
          ApiRest()
            .get(`/bi/event/series/${name.toLowerCase()}/${vm.agentId}?start=${start}&stop=${stop}&step=${step}`)
            .then(({ data }) => {
              callback(
                null,
                data.map(item => item.freq)
              )
            })

          if (isNaN(last)) last = start
          while (last < stop) {
            last += step
            // value = Math.max(-10, Math.min(10, value + 0.8 * Math.random() - 0.4 + 0.2 * Math.cos((i += 1 * 0.02))))
            values.push(value)
          }
          values = values.slice((start - stop) / step)
        }, name)
      }
    },
    setDimensions (animate = true) {
      this.graphOptions.style.marginTop = 0
      if (!this.$el.parentElement) {
        return false
      }
      if (
        this.$el.parentElement.clientWidth <= this.$el.parentElement.clientHeight ||
        this.$el.parentElement.clientHeight === 0
      ) {
        this.graphOptions.size.w = `${this.$el.parentElement.clientWidth}`
        if (this.$el.parentElement.clientHeight !== 0) {
          this.graphOptions.style.marginTop =
            (this.$el.parentElement.clientHeight - this.$el.parentElement.clientWidth - 15) / 2
        }
      } else {
        this.graphOptions.size.w = `${this.$el.parentElement.clientHeight}`
      }
      this.graphOptions.size.h = this.graphOptions.size.w
    }
  },
  mounted () {
    this.debounceResize = _.debounce(this.setDimensions, 500)
    // window resize event
    window.addEventListener('resize', this.debounceResize)
  },
  created () {
    if (!_.isEmpty(this.agentId)) {
      this.isLoading = true
      setTimeout(() => {
        this.isLoading = false
        this.setDimensions(false)
        this.renderChart()
      }, 300)
    }
  },
  beforeDestroy () {
    // remove event listeners when exiting view
    let svg = this.$d3.select(`#horizon-chart-${this.agentIdLabel}`)
    svg.selectAll('*').remove()

    window.removeEventListener('resize', this.debounceResize)
  },
  components: {}
}
</script>

<style scoped>
@import 'assets/styles/_variables.css';

.horizon-chart {
  height: 100%;
  width: fit-content;
  margin-left: 20px;
  position: relative;

  >>> svg {
    g {
      font-family: 'Muli', sans-serif !important;
    }
  }
}

>>> .axis {
  font: 10px 'Muli', sans-serif;
  position: fixed;
  pointer-events: none;
  z-index: 2;
}

>>> .axis text {
  -webkit-transition: fill-opacity 250ms linear;
}

>>> .axis path {
  display: none;
}

>>> .axis line {
  stroke: #000;
  shape-rendering: crispEdges;
}

>>> .axis.top {
  background-image: linear-gradient(top, #fff 0%, rgba(255, 255, 255, 0) 100%);
  top: 0px;
  position: relative;
  padding: 0 0 24px 0;
}

>>> .axis.bottom {
  background-image: linear-gradient(bottom, #fff 0%, rgba(255, 255, 255, 0) 100%);
  padding: 24px 0 0 0;
  position: relative;
}

>>> .horizon {
  border-top: solid 1px var(--border-color);
  border-bottom: solid 1px var(--border-color);
  overflow: hidden;
  position: relative;
}

>>> .horizon + .horizon {
  border-top: none;
}

>>> .horizon canvas {
  display: block;
}

>>> .horizon .title,
>>> .horizon .value {
  bottom: 0;
  line-height: 60px;
  margin: 0 6px;
  position: absolute;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
  white-space: nowrap;
}

>>> .horizon .title {
  left: -5px;
  top: 1px;
  line-height: 15px;
  padding: 5px 5px 5px 5px;
  bottom: unset;
  vertical-align: middle;
  background: transparent;
  color: black;
}

>>> .horizon .value {
  right: 30px;
}

>>> .line {
  background: var(--border-color);
  z-index: 2;
}
</style>



// WEBPACK FOOTER //
// src/components/chart/HorizonChart.vue